// 1-Projects/core4-masterclass/viewer/assets/mc-core.js
/* Core 4 Masterclass viewer: pure logic, no DOM. Loaded before mc-page.js; unit-tested with node --test. */
(function (root) {
  'use strict';
  const OFFER_DAYS = 21;
  const DAY = 86400000;
  const CODE_RE = /^[A-Za-z0-9_-]{4,40}$/;
  const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const MILESTONES = [25, 50, 75, 95];

  function parseTime(s) {
    if (typeof s === 'number') return s;
    const parts = String(s == null ? '' : s).trim().split(':');
    if (parts.length > 3 || parts.some(p => !/^\d+$/.test(p))) return NaN;
    return parts.reduce((acc, p) => acc * 60 + parseInt(p, 10), 0);
  }

  function formatTime(sec) {
    sec = Math.max(0, Math.floor(sec || 0));
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    const ss = String(s).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
  }

  function parseParams(search) {
    const p = new URLSearchParams(search || '');
    const c = p.get('c'), d = p.get('d');
    if (!c || !CODE_RE.test(c)) return { code: null, issuedAt: null };
    let issuedAt = null;
    if (d && DATE_RE.test(d)) {
      const t = Date.parse(d + 'T00:00:00Z');
      if (!Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === d) issuedAt = t;
    }
    return { code: c, issuedAt };
  }

  function resolveAccess({ params = {}, stored = {}, identified = false, now }) {
    let code = stored.code || null;
    let issuedAt = stored.issuedAt || null;
    if (params.code) {
      if (params.code !== code) { code = params.code; issuedAt = params.issuedAt || now; }
      else if (params.issuedAt) { issuedAt = issuedAt ? Math.min(issuedAt, params.issuedAt) : params.issuedAt; }
      else if (!issuedAt) { issuedAt = now; }
    }
    const unlocked = !!(params.code || stored.unlocked || identified);
    return { unlocked, code, issuedAt: code ? issuedAt : null };
  }

  // C1: decide unlock from the link or the device alone, so a blocked Klaviyo script can never hold the page locked
  const quickAccess = ({ params = {}, stored = {} }) => !!(params.code || stored.unlocked);

  const expiresAt = issuedAt => issuedAt + OFFER_DAYS * DAY;

  function offerState({ code, issuedAt, now }) {
    if (!code) return 'pending';
    if (issuedAt && now >= expiresAt(issuedAt)) return 'expired';
    return 'active';
  }

  const daysLeft = (issuedAt, now) => Math.max(0, Math.ceil((expiresAt(issuedAt) - now) / DAY));

  // src = the lesson's position in the theme-editor block list (matches the button's data-index);
  // index = its position after sorting. Bad times and duplicate start times are dropped.
  function sortLessons(raw) {
    const seen = new Set();
    return (raw || [])
      .map((l, src) => ({ title: l.title || '', start: parseTime(l.start), color: l.color || '', isOffer: !!l.isOffer, src }))
      .filter(l => Number.isFinite(l.start))
      .sort((a, b) => a.start - b.start || a.src - b.src)
      .filter(l => (seen.has(l.start) ? false : seen.add(l.start)))
      .map((l, index) => ({ ...l, index }));
  }

  function currentLesson(lessons, t) {
    let idx = -1;
    for (const l of lessons) { if (l.start <= t) idx = l.index; else break; }
    return idx;
  }

  const lessonEnd = (lessons, i, duration) => (i + 1 < lessons.length ? lessons[i + 1].start : duration);

  function isLessonWatched(playedSecs, lessons, i, duration) {
    const span = lessonEnd(lessons, i, duration) - lessons[i].start;
    return span > 0 && playedSecs >= 0.8 * span;
  }


  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  // Section notes markup -> HTML. Escapes first, so only the markup below can produce tags.
  // '# h' subhead · '- x' bullet · '> x' try-this box · other line = paragraph · **bold** · [m:ss] = jump button
  function notesHtml(text) {
    const inline = s => esc(s)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[(\d{1,2}:\d{2}(?::\d{2})?)\]/g, (m, t) => `<button type="button" class="mc-ts" data-seek="${parseTime(t)}">${t}</button>`);
    const out = []; let list = null;
    String(text == null ? '' : text).split(/\r?\n/).forEach(raw => {
      const line = raw.trim();
      if (line.startsWith('- ')) { (list = list || []).push(`<li>${inline(line.slice(2))}</li>`); return; }
      if (list) { out.push(`<ul>${list.join('')}</ul>`); list = null; }
      if (!line) return;
      if (line.startsWith('# ')) out.push(`<h4>${inline(line.slice(2))}</h4>`);
      else if (line.startsWith('> ')) out.push(`<div class="mc-try"><span>Try this</span>${inline(line.slice(2))}</div>`);
      else out.push(`<p>${inline(line)}</p>`);
    });
    if (list) out.push(`<ul>${list.join('')}</ul>`);
    return out.join('');
  }

  const milestonesCrossed = (prevMax, pct) => MILESTONES.filter(m => m > prevMax && m <= pct);

  const discountUrl = (code, redirectPath) =>
    `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirectPath || '/')}`;

  const MC = { OFFER_DAYS, quickAccess, parseTime, formatTime, parseParams, resolveAccess, offerState, expiresAt, daysLeft,
    sortLessons, currentLesson, lessonEnd, isLessonWatched, milestonesCrossed, discountUrl, notesHtml };
  if (typeof module !== 'undefined' && module.exports) module.exports = MC;
  else root.MC = MC;
})(typeof window !== 'undefined' ? window : globalThis);
